// ActionItems — surfaces action items extracted from meeting transcripts
// AND (Phase 2, W3) call transcripts. Owner can mark items done or
// dismissed via UPDATE through the meeting_action_items RLS policy.
//
// Read shape: meeting_action_items joined to BOTH the parent meeting
// AND the parent call (per migration 0019's source/source_call_id/
// meeting_id triple-source schema). The source column distinguishes
// origin: 'meeting' | 'call' | 'email'.
//
// Mutation shape: UPDATE meeting_action_items SET status=... WHERE id=...
// runs as the owner via Supabase Auth — the owner_update RLS policy
// (migration 0010) gates the write.
//
// UI rhythm (post-prototype-alignment polish):
//   - hero PageStatsRow with Open / Due this week / Completed today,
//     counts derived client-side from the existing query (no new reads).
//   - sectioned list (Pending / Completed / Dismissed) rather than a
//     status filter — source filter chips remain at the top of the list.
//   - is_for_owner rows get a left-rail forest accent, non-owner rows
//     show the assignee chip with more visual weight.
//   - action buttons stack below the row on viewports ≤520px (sm
//     breakpoint = 640px is wider than we need; flex-col → sm:flex-row
//     is the standard ladder).

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { motion } from 'framer-motion';
import {
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleSlash,
  ListChecks,
  MessageSquare,
  Phone,
  Sun,
  Video,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import GlassCard from '@/components/shared/GlassCard';
import PageStatsRow from '@/components/shared/PageStatsRow';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { resolveRecipients } from '@/lib/actionItemEmail';
import { firstNonBlank } from '@/utils/firstNonBlank';
import { SendActionItemEmailButton } from '@/components/action-items/SendActionItemEmailButton';

type ActionItemStatus = 'open' | 'done' | 'dismissed';
type ActionItemSource = 'meeting' | 'call' | 'email' | 'message';

type ActionItemRow = {
  id: string;
  text: string;
  assignee_name: string | null;
  assignee_email: string | null;
  is_for_owner: boolean;
  due_date: string | null;
  status: ActionItemStatus;
  source: ActionItemSource;
  created_at: string;
  updated_at: string | null;
  meeting_id: string | null;
  source_call_id: string | null;
  source_message_id: string | null;
  meetings: {
    id: string;
    title: string | null;
    started_at: string;
    meeting_participants: {
      email: string | null;
      name: string | null;
      represents_owner: boolean;
    }[];
  } | null;
  calls: {
    id: string;
    counterparty_handle: string | null;
    occurred_at: string;
  } | null;
  messages: {
    id: string;
    source_channel: string;
    counterparty_name: string | null;
    counterparty_handle: string | null;
    thread_label: string | null;
    occurred_at: string;
  } | null;
};

const SELECT_SHAPE =
  'id, text, assignee_name, assignee_email, is_for_owner, due_date, status, source, created_at, updated_at, meeting_id, source_call_id, source_message_id, meetings(id, title, started_at, meeting_participants(email, name, represents_owner)), calls(id, counterparty_handle, occurred_at), messages(id, source_channel, counterparty_name, counterparty_handle, thread_label, occurred_at)';

async function fetchActionItems(): Promise<ActionItemRow[]> {
  const { data, error } = await supabase
    .from('meeting_action_items')
    .select(SELECT_SHAPE)
    .order('created_at', { ascending: false })
    .limit(100);
  if (error) throw error;
  return (data as unknown as ActionItemRow[]) ?? [];
}

const Filter = ({
  label,
  active,
  count,
  onClick,
}: {
  label: string;
  active: boolean;
  count: number;
  onClick: () => void;
}) => (
  <button
    onClick={onClick}
    aria-pressed={active}
    className={`px-3 py-1.5 text-xs uppercase tracking-wide rounded-full transition-all ${
      active
        ? 'bg-forest text-primary-foreground shadow-sm'
        : 'neo-chip text-muted-foreground hover:text-foreground'
    }`}
  >
    {label} <span className="ml-1 font-mono opacity-70">{count}</span>
  </button>
);

// Derived stat helpers — pure, client-side, run against the in-memory
// rows. We deliberately avoid issuing additional Supabase reads (per
// scope: "no new queries"). The semantics:
//   - openCount: status='open'
//   - dueThisWeekCount: status='open' AND due_date present AND
//     due_date <= today + 7 days (inclusive of today, inclusive of day 7).
//     due_date is a 'YYYY-MM-DD' string in DB; lexical compare works
//     because the format is sortable.
//   - completedTodayCount: status='done' AND updated_at falls within
//     today in UTC. updated_at is the canonical "when did this row last
//     change" timestamp; created_at would over-include items that have
//     always been done.
function todayUtcDateString(): string {
  return new Date().toISOString().slice(0, 10);
}

function dateStringPlusDays(base: string, days: number): string {
  // base is 'YYYY-MM-DD'. Use a UTC Date to avoid local-tz drift; this
  // is a window comparison, not a wall-clock claim about the user's
  // perception of "this week".
  const d = new Date(`${base}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function isWithinUtcToday(iso: string | null): boolean {
  if (!iso) return false;
  const today = todayUtcDateString();
  return iso.slice(0, 10) === today;
}

const ActionItems = () => {
  const qc = useQueryClient();
  const itemsQ = useQuery({ queryKey: ['action_items'], queryFn: fetchActionItems });

  // Source filter stays — orthogonal to status sectioning.
  const [sourceFilter, setSourceFilter] = useState<ActionItemSource | 'all'>('all');
  // Dismissed section is collapsed-by-default per scope.
  const [showDismissed, setShowDismissed] = useState(false);

  const counts = useMemo(() => {
    const all = itemsQ.data;
    if (!all) {
      return {
        all: 0,
        open: 0,
        done: 0,
        dismissed: 0,
        meeting: 0,
        call: 0,
        email: 0,
        message: 0,
        dueThisWeek: 0,
        completedToday: 0,
      };
    }
    const today = todayUtcDateString();
    const horizon = dateStringPlusDays(today, 7);
    return {
      all: all.length,
      open: all.filter((r) => r.status === 'open').length,
      done: all.filter((r) => r.status === 'done').length,
      dismissed: all.filter((r) => r.status === 'dismissed').length,
      meeting: all.filter((r) => r.source === 'meeting').length,
      call: all.filter((r) => r.source === 'call').length,
      email: all.filter((r) => r.source === 'email').length,
      message: all.filter((r) => r.source === 'message').length,
      dueThisWeek: all.filter(
        (r) => r.status === 'open' && r.due_date !== null && r.due_date <= horizon,
      ).length,
      completedToday: all.filter((r) => r.status === 'done' && isWithinUtcToday(r.updated_at))
        .length,
    };
  }, [itemsQ.data]);

  // Apply source filter then group by status into stable sections.
  const sectioned = useMemo(() => {
    let rows = itemsQ.data ?? [];
    if (sourceFilter !== 'all') {
      rows = rows.filter((r) => r.source === sourceFilter);
    }
    return {
      pending: rows.filter((r) => r.status === 'open'),
      completed: rows.filter((r) => r.status === 'done'),
      dismissed: rows.filter((r) => r.status === 'dismissed'),
    };
  }, [itemsQ.data, sourceFilter]);

  const setStatus = useMutation({
    mutationFn: async (args: { id: string; status: ActionItemStatus }) => {
      const { error } = await supabase
        .from('meeting_action_items')
        .update({ status: args.status })
        .eq('id', args.id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['action_items'] }),
  });

  const rowPending = (id: string): boolean => setStatus.isPending && setStatus.variables?.id === id;

  // Stats: loading → undefined so PageStatsRow shows em-dash; loaded →
  // numbers. We pass derived counts only — no new query.
  const statsValues = itemsQ.data
    ? {
        open: counts.open,
        dueThisWeek: counts.dueThisWeek,
        completedToday: counts.completedToday,
      }
    : { open: undefined, dueThisWeek: undefined, completedToday: undefined };

  return (
    <div className="space-y-6">
      <motion.header
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="flex items-start justify-between gap-4"
      >
        <div>
          <h1 className="text-3xl font-serif font-semibold tracking-tight text-foreground">
            Action Items
          </h1>
          <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
            {counts.open === 0
              ? counts.all === 0
                ? 'No action items yet. Items extracted from meetings, calls + your outgoing messages surface here.'
                : 'No open items. New ones surface within ~60s of a meeting, call, or message.'
              : `${counts.open} open · ${counts.done} done · ${counts.dismissed} dismissed · ${counts.meeting} from meetings · ${counts.call} from calls${counts.message > 0 ? ` · ${counts.message} from messages` : ''}`}
          </p>
        </div>
      </motion.header>

      <PageStatsRow
        stats={[
          {
            label: 'Open',
            value: statsValues.open,
            icon: ListChecks,
            iconClassName: 'text-forest',
            secondary: counts.all > 0 ? `of ${counts.all} total` : undefined,
          },
          {
            label: 'Due this week',
            value: statsValues.dueThisWeek,
            icon: ChevronRight,
            iconClassName: 'text-attention',
            valueClassName:
              statsValues.dueThisWeek && statsValues.dueThisWeek > 0
                ? 'text-attention'
                : 'text-foreground',
            secondary: 'next 7 days',
          },
          {
            label: 'Completed today',
            value: statsValues.completedToday,
            icon: Sun,
            iconClassName: 'text-sage',
            valueClassName:
              statsValues.completedToday && statsValues.completedToday > 0
                ? 'text-sage'
                : 'text-foreground',
            secondary: 'since 00:00 UTC',
          },
        ]}
      />

      <div className="flex flex-wrap gap-2">
        <Filter
          label="All sources"
          active={sourceFilter === 'all'}
          count={counts.all}
          onClick={() => setSourceFilter('all')}
        />
        <Filter
          label="Meetings"
          active={sourceFilter === 'meeting'}
          count={counts.meeting}
          onClick={() => setSourceFilter('meeting')}
        />
        <Filter
          label="Calls"
          active={sourceFilter === 'call'}
          count={counts.call}
          onClick={() => setSourceFilter('call')}
        />
        {counts.email > 0 && (
          <Filter
            label="Emails"
            active={sourceFilter === 'email'}
            count={counts.email}
            onClick={() => setSourceFilter('email')}
          />
        )}
        {counts.message > 0 && (
          <Filter
            label="Messages"
            active={sourceFilter === 'message'}
            count={counts.message}
            onClick={() => setSourceFilter('message')}
          />
        )}
      </div>

      <section className="space-y-6">
        {itemsQ.isLoading && <p className="text-sm text-muted-foreground">Loading action items…</p>}
        {itemsQ.isError && (
          <GlassCard hover={false} className="neo-alert">
            <p className="text-sm text-alert">Failed to load: {(itemsQ.error as Error).message}</p>
          </GlassCard>
        )}

        {!itemsQ.isLoading &&
          !itemsQ.isError &&
          sectioned.pending.length === 0 &&
          sectioned.completed.length === 0 &&
          sectioned.dismissed.length === 0 && (
            <GlassCard hover={false} variant="inset">
              <p className="text-sm text-muted-foreground">No items in this filter.</p>
            </GlassCard>
          )}

        {sectioned.pending.length > 0 && (
          <SectionGroup label="Pending" count={sectioned.pending.length} tone="default">
            {sectioned.pending.map((row) => (
              <ActionItemRowCard
                key={row.id}
                row={row}
                pending={rowPending(row.id)}
                onMarkDone={() => setStatus.mutate({ id: row.id, status: 'done' })}
                onDismiss={() => setStatus.mutate({ id: row.id, status: 'dismissed' })}
                onReopen={() => setStatus.mutate({ id: row.id, status: 'open' })}
              />
            ))}
          </SectionGroup>
        )}

        {sectioned.completed.length > 0 && (
          <SectionGroup label="Completed" count={sectioned.completed.length} tone="muted">
            {sectioned.completed.map((row) => (
              <ActionItemRowCard
                key={row.id}
                row={row}
                pending={rowPending(row.id)}
                onMarkDone={() => setStatus.mutate({ id: row.id, status: 'done' })}
                onDismiss={() => setStatus.mutate({ id: row.id, status: 'dismissed' })}
                onReopen={() => setStatus.mutate({ id: row.id, status: 'open' })}
              />
            ))}
          </SectionGroup>
        )}

        {sectioned.dismissed.length > 0 && (
          <section className="space-y-2">
            <button
              type="button"
              onClick={() => setShowDismissed((s) => !s)}
              aria-expanded={showDismissed}
              className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-muted-foreground hover:text-foreground transition-colors"
            >
              {showDismissed ? (
                <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" />
              ) : (
                <ChevronRight className="w-3.5 h-3.5" aria-hidden="true" />
              )}
              {showDismissed
                ? `Hide ${sectioned.dismissed.length} dismissed`
                : `Show ${sectioned.dismissed.length} dismissed`}
            </button>
            {showDismissed && (
              <div className="space-y-3">
                {sectioned.dismissed.map((row) => (
                  <ActionItemRowCard
                    key={row.id}
                    row={row}
                    pending={rowPending(row.id)}
                    onMarkDone={() => setStatus.mutate({ id: row.id, status: 'done' })}
                    onDismiss={() => setStatus.mutate({ id: row.id, status: 'dismissed' })}
                    onReopen={() => setStatus.mutate({ id: row.id, status: 'open' })}
                  />
                ))}
              </div>
            )}
          </section>
        )}
      </section>
    </div>
  );
};

function SectionGroup({
  label,
  count,
  tone,
  children,
}: {
  label: string;
  count: number;
  tone: 'default' | 'muted';
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-2">
      <h2
        className={`text-[11px] uppercase tracking-wide ${
          tone === 'muted' ? 'text-muted-foreground/70' : 'text-muted-foreground'
        }`}
      >
        {label} <span className="font-mono opacity-70">{count}</span>
      </h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

type ActionItemRowCardProps = {
  row: ActionItemRow;
  pending: boolean;
  onMarkDone: () => void;
  onDismiss: () => void;
  onReopen: () => void;
};

// Source pill tone — keep parallel to category-tone helper in Inbox.tsx
// so the design language stays consistent across pages.
function sourceTone(source: ActionItemSource): { bg: string; text: string; Icon: typeof Video } {
  switch (source) {
    case 'meeting':
      return { bg: 'bg-forest-soft', text: 'text-forest', Icon: Video };
    case 'call':
      return { bg: 'bg-sage/10', text: 'text-sage', Icon: Phone };
    case 'message':
      return { bg: 'bg-sky-500/10', text: 'text-sky-600', Icon: MessageSquare };
    case 'email':
    default:
      return { bg: 'bg-learning/10', text: 'text-learning', Icon: Video };
  }
}

function ActionItemRowCard({
  row,
  pending,
  onMarkDone,
  onDismiss,
  onReopen,
}: ActionItemRowCardProps) {
  const { user } = useAuth();
  const ownerId = user?.id ?? null;
  const isOpen = row.status === 'open';
  const isDone = row.status === 'done';
  const isDismissed = row.status === 'dismissed';
  const tone = sourceTone(row.source);
  const Icon = tone.Icon;

  // Emailable when a recipient resolves: the item's assignee (a follow-up —
  // "you mentioned you'd… — status?"), or the owner himself for "for you"
  // items (a self-reminder from reverra@agentmail.to). Open items only.
  const emailCandidates = resolveRecipients(
    row,
    row.meetings?.meeting_participants ?? [],
    user?.email ?? null,
  );

  const contextNode = (() => {
    if (row.source === 'meeting' && row.meetings) {
      return (
        <span className="flex items-center gap-1">
          <Icon className="w-3 h-3" />
          <span className="truncate max-w-[260px]">{row.meetings.title ?? '(no title)'}</span>
          <span className="font-mono opacity-70">
            · {formatDistanceToNow(new Date(row.meetings.started_at), { addSuffix: true })}
          </span>
        </span>
      );
    }
    if (row.source === 'call' && row.calls) {
      return (
        <span className="flex items-center gap-1">
          <Icon className="w-3 h-3" />
          <span className="truncate max-w-[260px]">
            {firstNonBlank(row.calls.counterparty_handle) ?? '(unknown caller)'}
          </span>
          <span className="font-mono opacity-70">
            · {formatDistanceToNow(new Date(row.calls.occurred_at), { addSuffix: true })}
          </span>
        </span>
      );
    }
    if (row.source === 'message' && row.messages) {
      // firstNonBlank, not ?? — a blank thread_label (macOS 1:1 chats) would
      // otherwise shadow the resolved contact name.
      const who =
        firstNonBlank(
          row.messages.thread_label,
          row.messages.counterparty_name,
          row.messages.counterparty_handle,
        ) ?? '(unknown thread)';
      return (
        <span className="flex items-center gap-1">
          <Icon className="w-3 h-3" />
          <span className="truncate max-w-[260px]">
            {who} <span className="uppercase opacity-60">· {row.messages.source_channel}</span>
          </span>
          <span className="font-mono opacity-70">
            · {formatDistanceToNow(new Date(row.messages.occurred_at), { addSuffix: true })}
          </span>
        </span>
      );
    }
    return null;
  })();

  // Owner-accent: the left-rail bar fires only on is_for_owner items in
  // the pending state. Once done/dismissed we drop the accent — the
  // opacity treatment already de-emphasizes the row.
  const ownerAccent =
    row.is_for_owner && isOpen
      ? 'relative before:absolute before:left-1 before:top-3 before:bottom-3 before:w-1 before:rounded-r before:bg-forest'
      : '';

  return (
    <GlassCard
      hover={false}
      className={`transition-all ${pending ? 'opacity-60' : ''} ${
        isDone ? 'opacity-70' : isDismissed ? 'opacity-50' : ''
      } ${ownerAccent}`}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="mt-1 shrink-0">
            {isOpen && <span className="block w-2.5 h-2.5 rounded-full bg-attention" />}
            {isDone && <CheckCircle2 className="w-4 h-4 text-sage" />}
            {isDismissed && <CircleSlash className="w-4 h-4 text-muted-foreground" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span
                className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full neo-chip ${tone.bg} ${tone.text}`}
              >
                {row.source}
              </span>
              {row.is_for_owner ? (
                <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full neo-chip bg-forest-soft text-forest">
                  for you
                </span>
              ) : (
                <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full neo-chip text-foreground font-medium">
                  → {row.assignee_name ?? row.assignee_email ?? 'unassigned'}
                </span>
              )}
            </div>
            <p
              className={`text-sm text-foreground leading-snug ${
                isDone || isDismissed ? 'line-through decoration-muted-foreground/50' : ''
              }`}
            >
              {row.text}
            </p>
            <div className="mt-1.5 flex items-center gap-3 text-[11px] text-muted-foreground flex-wrap">
              {row.due_date && <span>due {row.due_date}</span>}
              {contextNode}
            </div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1 sm:self-start">
          {isOpen && ownerId && emailCandidates.length > 0 && (
            <SendActionItemEmailButton
              actionItemId={row.id}
              sourceMeetingId={row.meeting_id}
              candidates={emailCandidates}
              ownerId={ownerId}
              selfReminder={row.is_for_owner}
            />
          )}
          {isOpen ? (
            <>
              <Button
                size="sm"
                variant="ghost"
                disabled={pending}
                onClick={onDismiss}
                aria-label="Dismiss action item"
              >
                <CircleSlash className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                disabled={pending}
                onClick={onMarkDone}
                aria-label="Mark action item done"
              >
                <Check className="w-4 h-4 mr-1" />
                Done
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              variant="outline"
              disabled={pending}
              onClick={onReopen}
              aria-label="Re-open action item"
            >
              Re-open
            </Button>
          )}
        </div>
      </div>
    </GlassCard>
  );
}

export default ActionItems;
